import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import { supabase } from "../../../lib/supabaseClient";

const ImageUploadModal = ({ onClose, onAddImages }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.slice(0, 10);

    // Réinitialiser les erreurs précédentes
    setError(null);

    const checkedFiles = validFiles.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      const maxSize = 30 * 1024 * 1024; // 30MB

      if (!validTypes.includes(file.type)) {
        setError(t("artworkModals.imageUpload.errors.unsupported", { name: file.name }));
        return false;
      }

      if (file.size > maxSize) {
        setError(t("artworkModals.imageUpload.errors.tooLarge", { name: file.name }));
        return false;
      }

      return true;
    });

    setFiles(checkedFiles);
    console.log("Fichiers sélectionnés:", checkedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = droppedFiles.slice(0, 10);
      
      // Réinitialiser les erreurs précédentes
      setError(null);
      
      const checkedFiles = validFiles.filter(file => {
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxSize = 30 * 1024 * 1024; // 30MB

        if (!validTypes.includes(file.type)) {
          setError(t("artworkModals.imageUpload.errors.unsupported", { name: file.name }));
          return false;
        }

        if (file.size > maxSize) {
          setError(t("artworkModals.imageUpload.errors.tooLarge", { name: file.name }));
          return false;
        }

        return true;
      });
      
      setFiles(checkedFiles);
      console.log("Fichiers déposés:", checkedFiles);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError(t("artworkModals.imageUpload.errors.required"));
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);
    
    console.log("Début de l'upload de", files.length, "fichiers");

    try {
      const uploadedImages = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
// Générer un nom de fichier unique
        const cleanName = file.name
  .normalize("NFD")                         // enlève les accents
  .replace(/[\u0300-\u036f]/g, "")         // caractères diacritiques
  .replace(/[^a-zA-Z0-9.\-_]/g, "_");      // remplace tout caractère non valide
const fileName = `${uuidv4()}-${cleanName}`;

      // IMPORTANT: Définir correctement le chemin dans le bucket
      // Au lieu de:
      // const filePath = `artworks-images/${fileName}`;
      
      // Utilisez simplement:
      const filePath = fileName;  
        console.log(`Uploading file ${i+1}/${files.length}: ${fileName}`);
        
        // Télécharger le fichier vers Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('artworks-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error("Erreur lors de l'upload:", uploadError);
          setError(t("artworkModals.imageUpload.errors.uploadFile", { name: file.name, message: uploadError.message }));
          continue;
        }
        
        console.log("Fichier téléchargé avec succès:", uploadData);
        
        // Obtenir l'URL publique
        const { data } = supabase.storage
          .from('artworks-images')
          .getPublicUrl(filePath);
        
        if (!data || !data.publicUrl) {
          console.error("URL publique non disponible pour", filePath);
          setError(t("artworkModals.imageUpload.errors.publicUrl", { name: file.name }));
          continue;
        }
        
        console.log("URL publique obtenue:", data.publicUrl);
        
        // Ajouter l'image à la liste
        uploadedImages.push({
          id: uuidv4(),
          url: data.publicUrl,
	// Ajoutez path pour être cohérent
        path: data.publicUrl,
          name: file.name,
          size: file.size,
          type: file.type,
          isPrimary: uploadedImages.length === 0, // La première image est principale
          uploadedAt: new Date().toISOString()
        });
        
        // Mettre à jour la progression
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      console.log("Images téléchargées:", uploadedImages);
      
      if (uploadedImages.length > 0) {
        onAddImages(uploadedImages);
        onClose();
      } else {
        setError(t("artworkModals.imageUpload.errors.noneUploaded"));
      }
    } catch (error) {
      console.error("Erreur lors de l'upload des images:", error);
      setError(t("artworkModals.imageUpload.errors.upload"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl">
        <h2 className="text-xl font-bold mb-4">{t("artworkModals.imageUpload.title")}</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        <div 
          className="border-2 border-dashed border-gray-300 p-8 text-center mb-4 rounded"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />

          <p className="text-gray-700 mb-4">{t("artworkModals.imageUpload.dropzone")}</p>
          <button 
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            disabled={uploading}
          >
            {t("artworkModals.common.browse")}
          </button>
          <p className="text-xs text-gray-500 mt-2">{t("artworkModals.imageUpload.limits")}</p>
          <p className="text-xs text-gray-500">{t("artworkModals.imageUpload.formats")}</p>
        </div>

        {files.length > 0 && (
          <div className="mb-4">
            <h3 className="font-medium mb-2">{t("artworkModals.imageUpload.selected", { count: files.length })}</h3>
            <div className="max-h-40 overflow-y-auto">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between py-1 border-b">
                  <div className="flex items-center">
                    <div className="w-8 h-8 mr-2 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={file.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <span className="text-sm text-ellipsis overflow-hidden">{file.name}</span>
                  </div>
                  <span className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {uploading && (
          <div className="mb-4">
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium">{t("artworkModals.imageUpload.progress")}</span>
              <span className="text-sm font-medium">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
            disabled={uploading}
          >
            {t("artworkModals.common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={uploading || files.length === 0}
          >
            {uploading ? t("artworkModals.common.uploading") : t("artworkModals.imageUpload.actions.upload")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;